import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

export interface CaseTeamMember {
  assignment_id: string;
  department: string;
  assignment_kind: 'Primary' | 'Covering';
  staff_user_id: string;
  staff_name: string;
  staff_email: string;
  assigned_on: string;
  covers_until: string | null;
  granted_reason: string | null;
  ended_on: string | null;
  is_current: boolean;
  granted_by: string | null;
  is_live: boolean;
  days_left: number | null;
}

export interface CaseTeam {
  team: CaseTeamMember[];
  /** Whether the viewer may change any of it — the route decides, not the tier. */
  may_assign: boolean;
  /** 'Deceased' | 'Discharged' when the case has ended, else null. */
  case_ended: string | null;
}

export interface CaseManager {
  id: string;
  full_name: string;
  email: string;
  caseload: number;
}

/**
 * Who holds this case and who is covering it.
 *
 * Every case manager can see this; only the Director can change it, and
 * `may_assign` comes back from the same call rather than being guessed from
 * the viewer's tier — the Director of Social Work is tier 3 and the rota is
 * theirs, which no tier rule would get right.
 */
export function useCaseTeam(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'case-team'],
    queryFn: () => apiFetch<CaseTeam>(`/clients/${clientId}/case-team`, getToken),
    ...queryDefaults,
  });
}

/** The pick list, asked for only once somebody opens the form. */
export function useCaseManagers(department: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['case-managers', department],
    queryFn: () =>
      apiFetch<{ case_managers: CaseManager[] }>(
        `/case-managers?department=${encodeURIComponent(department)}`,
        getToken,
      ),
    ...queryDefaults,
    staleTime: 5 * 60_000,
    enabled,
  });
}

/**
 * Every change to the team, through one mutation keyed by path.
 *
 * All five write the same two things — who holds the case and who is lent it —
 * and all five invalidate the same queries: the client (its header names the
 * case manager), the visits board (its cards are grouped by assignment) and
 * the caseload counts.
 */
function useTeamMutation<TBody>(clientId: string, path: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) =>
      apiFetch<CaseTeam>(`/clients/${clientId}/${path}`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['case-managers'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
    },
  });
}

export const useAssignCaseManager = (clientId: string) =>
  useTeamMutation<{ staff_user_id: string; department?: string; notes?: string }>(
    clientId,
    'assignment',
  );

export const useGrantCoverage = (clientId: string) =>
  useTeamMutation<{
    staff_user_id: string;
    department?: string;
    days: number;
    reason: string;
  }>(clientId, 'coverage');

export const useExtendCoverage = (clientId: string) =>
  useTeamMutation<{ assignment_id: string; days: number }>(clientId, 'coverage/extend');

export const useEndCoverage = (clientId: string) =>
  useTeamMutation<{ assignment_id: string }>(clientId, 'coverage/end');

/** Bulk assignment from the visits board's unassigned card. */
export function useBulkAssign() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { client_ids: string[]; staff_user_id: string; department?: string }) =>
      apiFetch<{ assigned: number; requested: number; not_visible: number }>(
        '/assignments/bulk',
        getToken,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['client'] });
      void queryClient.invalidateQueries({ queryKey: ['case-managers'] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}
